/* eslint-disable @typescript-eslint/camelcase */
import {
  CredRow,
  CredRowErrors,
  CredRowStatuses,
  INITIAL_CREDROW,
  Credential,
  Loadable,
  idle,
  LoadableStatus
} from '@card/types';

import { UsersActionType, UsersAction } from '../../actions/userActions';
import {
  EstablishmentsActionType,
  EstablishmentsAction
} from '../settings/actions/establishmentsActions';
import { CredentialsActionType, CredentialsAction } from './actions';
import { CredentialUtils } from '@card/commonlib';

type ListSettingsType = {
  currentPage: number;
  totalPages: number;
};

type ShowPasswordType = {
  credentialId: string;
  password: string;
};

export type CredentialState = {
  credentialStatus: keyof CredRowStatuses;
  credentialError: keyof CredRowErrors;
  problemCredentialsCount: Loadable<number>;
  credRows: CredRow[] | undefined;
  credRowsBackup: CredRow[] | undefined;
  listSettings: ListSettingsType;
  credentialChanged: boolean;
  autoRenewAllCredentials: boolean;
  showPassword: ShowPasswordType | undefined;
  mercadopagoActivationUrl: string | undefined;
  fetching: boolean;
  error: string | undefined;
};

const initialState: CredentialState = {
  credentialStatus: INITIAL_CREDROW.status,
  credentialError: INITIAL_CREDROW.error,
  problemCredentialsCount: idle(),
  credRows: undefined,
  listSettings: {
    totalPages: 1,
    currentPage: 1
  },
  credRowsBackup: undefined,
  credentialChanged: false,
  autoRenewAllCredentials: false,
  showPassword: undefined,
  mercadopagoActivationUrl: undefined,
  fetching: false,
  error: undefined
};

function toggleCredentialRenewal(
  credentials: CredRow[] | undefined,
  automaticRenewal: boolean,
  credentialId: string | undefined = undefined
) {
  if (!credentials) {
    return undefined;
  }
  // set credential
  return credentials.map((credential) =>
    credentialId === undefined || credentialId === credential.credential_id
      ? {
          ...credential,
          automatic_renewal: automaticRenewal
        }
      : credential
  );
}

function formatCredentials(credentialsFromApi: Credential[]) {
  return credentialsFromApi.map((credential) => CredentialUtils.toCredentialsRow(credential));
}

export function credentialsReducers(
  state = initialState,
  action: CredentialsAction | EstablishmentsAction | UsersAction
): CredentialState {
  switch (action.type) {
    case CredentialsActionType.Validate:
      return {
        ...state,
        credentialStatus: 'VERIFYING'
      };
    case CredentialsActionType.SaveEditedCredential:
      return {
        ...state,
        credentialStatus: 'VERIFYING'
      };
    case CredentialsActionType.ValidateSuccess:
      return {
        ...state,
        credentialChanged: true,
        credentialStatus: 'CREATED'
      };
    case CredentialsActionType.ValidateError: {
      return {
        ...state,
        credentialStatus: 'NOT_VERIFIED',
        credentialError: action.payload
      };
    }
    case CredentialsActionType.SetStatus:
      return {
        ...state,
        credentialStatus: action.payload
      };
    case CredentialsActionType.SetError:
      return {
        ...state,
        credentialError: action.payload
      };
    case CredentialsActionType.FetchBlocked:
    case CredentialsActionType.Fetch:
      return {
        ...state,
        credRows: undefined,
        fetching: true,
        error: undefined
      };
    case CredentialsActionType.FetchBlockedSuccess:
    case CredentialsActionType.FetchSuccess:
      return {
        ...state,
        credRows: formatCredentials(action.payload.data),
        listSettings: {
          currentPage: parseInt(action.payload.current_page),
          totalPages: action.payload.total_pages
        },
        credentialChanged: false,
        fetching: false,
        error: undefined
      };
    case CredentialsActionType.FetchBlockedFailed:
    case CredentialsActionType.FetchFailed:
      return {
        ...state,
        fetching: false,
        error: action.payload.message
      };
    case EstablishmentsActionType.AssignShop: {
      const credRowsToSave =
        state.credRows &&
        state.credRows.map((credRow) => {
          return credRow.credential_id === action.payload.credentialId
            ? //Si es la credencial que estoy modificando, modifiquemos establishments, sino retornala
              {
                ...credRow,
                establishments: credRow.establishments.map((establishment) => {
                  return establishment.id === action.payload.establishmentId
                    ? //Si es el establishment que estoy modificando, modifiquemos su shop_id, sino retornalo
                      // eslint-disable-next-line @typescript-eslint/camelcase
                      { ...establishment, shop_id: action.payload.shopId }
                    : establishment;
                })
              }
            : credRow;
        });
      return {
        ...state,
        credRowsBackup: state.credRows,
        credRows: credRowsToSave
      };
    }
    case EstablishmentsActionType.AssignSuccess: {
      return {
        ...state,
        credRowsBackup: undefined
      };
    }
    case EstablishmentsActionType.AssignFailed: {
      return {
        ...state,
        credRows: state.credRowsBackup
      };
    }
    case CredentialsActionType.ToggleAutomaticRenewal: {
      return {
        ...state,
        credRowsBackup: state.credRows,
        credRows: toggleCredentialRenewal(
          state.credRows,
          action.payload.enableAutomaticRenewal,
          action.payload.credentialId
        )
      };
    }
    case CredentialsActionType.ToggleAutomaticRenewalSuccess: {
      return {
        ...state,
        credRowsBackup: undefined
      };
    }
    case CredentialsActionType.ToggleAutomaticRenewalFailed: {
      return {
        ...state,
        credRows: state.credRowsBackup
      };
    }
    case EstablishmentsActionType.AssignAllEstablishments: {
      const credRowsToSave =
        state.credRows &&
        state.credRows.map((credential) => {
          return credential.credential_id === action.payload.credentialId
            ? //Si es la credencial que estoy modificando, modifiquemos establishments, sino retornala
              {
                ...credential,
                // eslint-disable-next-line @typescript-eslint/camelcase
                has_unassigned_estabs: !action.payload.assignAll,
                establishments: credential.establishments.map((establishmentFromCredential) => {
                  const assignationFound = action.payload.assignations.find(
                    (assignation) => assignation.establishmentId === establishmentFromCredential.id
                  );
                  return assignationFound
                    ? {
                        ...establishmentFromCredential,
                        // eslint-disable-next-line @typescript-eslint/camelcase
                        shop_id: assignationFound.shopId
                      }
                    : establishmentFromCredential;
                })
              }
            : credential;
        });
      return {
        ...state,
        credRowsBackup: state.credRows,
        credRows: credRowsToSave
      };
    }
    case EstablishmentsActionType.AssignAllEstablishmentsSuccess: {
      return {
        ...state,
        credRowsBackup: undefined
      };
    }
    case EstablishmentsActionType.AssignAllEstablishmentsFailed: {
      return {
        ...state,
        credRows: state.credRowsBackup
      };
    }
    case CredentialsActionType.DeleteCredential: {
      const credentialsToSave =
        state.credRows &&
        state.credRows.filter((credential: CredRow) => {
          return credential.credential_id !== action.payload;
        });
      return {
        ...state,
        credRowsBackup: state.credRows,
        credRows: credentialsToSave
      };
    }
    case CredentialsActionType.DeleteCredentialSuccess: {
      return {
        ...state,
        credRowsBackup: undefined
      };
    }
    case CredentialsActionType.DeleteCredentialError: {
      return {
        ...state,
        credRows: state.credRowsBackup
      };
    }
    case UsersActionType.FetchSuccess:
      return {
        ...state,
        autoRenewAllCredentials: action.payload.automatic_renewal
      };
    case CredentialsActionType.ToggleAutoRenewAllCredentials: {
      const autoRenewal = action.payload;
      return {
        ...state,
        autoRenewAllCredentials: autoRenewal,
        credRowsBackup: state.credRows,
        credRows: toggleCredentialRenewal(state.credRows, autoRenewal)
      };
    }
    case CredentialsActionType.ToggleAutoRenewAllCredentialsFailed:
      return {
        ...state,
        autoRenewAllCredentials: !state.autoRenewAllCredentials
      };
    case CredentialsActionType.GetPassword:
      return {
        ...state,
        showPassword: undefined
      };
    case CredentialsActionType.GetPasswordSuccess: {
      return {
        ...state,
        showPassword: {
          credentialId: action.payload.credentialId,
          password: action.payload.password
        }
      };
    }
    case CredentialsActionType.GetMercadopagoLinkSuccess:
      return {
        ...state,
        mercadopagoActivationUrl: action.payload.mercadopagoActivationUrl
      };
    case CredentialsActionType.FetchBlockedCountStarted:
      return {
        ...state,
        problemCredentialsCount: {
          ...state.problemCredentialsCount,
          status: LoadableStatus.Fetching
        }
      };
    case CredentialsActionType.FetchBlockedCountFailed:
      return {
        ...state,
        problemCredentialsCount: {
          ...state.problemCredentialsCount,
          status: LoadableStatus.Error,
          error: action.payload
        }
      };
    case CredentialsActionType.FetchBlockedCountSuccess:
      return {
        ...state,
        problemCredentialsCount: {
          ...state.problemCredentialsCount,
          status: LoadableStatus.Fetched,
          data: action.payload.count
        }
      };
    default:
      return state;
  }
}

export default credentialsReducers;
